import {
  Directive,
  ElementRef,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Renderer2,
  SimpleChanges,
} from "@angular/core";
import { Subscription } from "rxjs";
import { AccountsService } from "../../../services/accounts.service";
import { PermissionResolverService } from "@services/permissions/permission-resolver.service";

@Directive({
  selector: "[dwsPermission],[dwsPermissionSectionBase]",
})
export class DwsPermissionDirective implements OnInit, OnChanges, OnDestroy {
  @Input() dwsPermission?: string;
  /** When set, evaluates section access via PermissionResolver (overrides dwsPermission if both set). */
  @Input() dwsPermissionSectionBase?: string;
  /** With dwsPermissionSectionBase: "view" = can open section; "edit" = can mutate. */
  @Input() dwsPermissionSectionMode: "view" | "edit" = "view";
  @Input() dwsPermissionDenyMode: "disable" | "hide" = "disable";

  /**
   *
   * @param permissionName returns the permission name of account requested by code
   * @param isGranted returns the permission granted by default array of permissions of account
   * @returns if return true, the permission is granted, if return false, the permission is denied
   */
  @Input() dwsPermissionCustomLogic: (
    permissionName: string,
    granted: boolean
  ) => boolean = (x: string, y: boolean) => y;

  private permissionSubscription?: Subscription;

  constructor(
    private elementRef: ElementRef,
    private accountsService: AccountsService,
    private permissionResolver: PermissionResolverService,
    private renderer: Renderer2
  ) {}

  ngOnInit() {
    this.internalEvaluatePermission();
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.internalEvaluatePermission();
  }

  internalEvaluatePermission() {
    if (this.permissionSubscription) {
      this.permissionSubscription.unsubscribe();
      this.permissionSubscription = undefined;
    }
    if (!this.dwsPermission && !this.dwsPermissionSectionBase) {
      this.acceptPermission();
      return;
    }

    const evaluatePermission = () => {
      let isGranted = false;
      if (this.dwsPermissionSectionBase) {
        isGranted =
          this.dwsPermissionSectionMode === "edit"
            ? this.permissionResolver.canEditSection(
                this.dwsPermissionSectionBase
              )
            : this.permissionResolver.canViewSection(
                this.dwsPermissionSectionBase
              );
      } else if (this.dwsPermission) {
        isGranted = this.accountsService.hasPermission(this.dwsPermission);
      }
      const permLabel = this.dwsPermissionSectionBase ?? this.dwsPermission ?? "";
      if (!this.dwsPermissionCustomLogic(permLabel, isGranted)) {
        this.denyPermission();
      } else {
        this.acceptPermission();
      }
    };

    evaluatePermission();

    this.permissionSubscription = this.accountsService.cachedPermissions$.subscribe(
      () => {
        evaluatePermission();
      }
    );
  }

  ngOnDestroy() {
    if (this.permissionSubscription) {
      this.permissionSubscription.unsubscribe();
    }
  }

  private denyPermission() {
    if (this.dwsPermissionDenyMode == "disable") {
      this.renderer.setProperty(
        this.elementRef.nativeElement,
        "disabled",
        true
      );
      try {
        const interactiveElements =
          this.elementRef.nativeElement.querySelectorAll(
            "button, a, input, select, textarea"
          );
        interactiveElements.forEach((el: HTMLElement) => {
          this.renderer.setProperty(el, "disabled", true);
          this.renderer.setStyle(el, "pointer-events", "none");
        });
      } catch (e) {
        console.error("Error disabling interactive elements", e);
      }
    } else {
      this.elementRef.nativeElement.style.visibility = "hidden";
    }
    this.renderer.setAttribute(
      this.elementRef.nativeElement,
      "dws-permission-denied",
      "true"
    );
  }

  private acceptPermission() {
    if (this.dwsPermissionDenyMode == "disable") {
      this.renderer.setProperty(
        this.elementRef.nativeElement,
        "disabled",
        false
      );
      try {
        const interactiveElements =
          this.elementRef.nativeElement.querySelectorAll(
            "button, a, input, select, textarea"
          );
        interactiveElements.forEach((el: HTMLElement) => {
          this.renderer.setProperty(el, "disabled", false);
          this.renderer.removeStyle(el, "pointer-events");
        });
      } catch (e) {
        console.error("Error enabling interactive elements", e);
      }
    } else {
      this.elementRef.nativeElement.style.visibility = "visible";
    }
    this.renderer.removeAttribute(
      this.elementRef.nativeElement,
      "dws-permission-denied"
    );
  }
}
